import './App.css';
import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import Lobby from './Lobby.js';

import Game from './Game.js';

import firebase from "./firebase"

import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

var provider = new firebase.auth.GoogleAuthProvider();

const boysNames = require('./boys.json').names;
const girlsNames = require('./girls.json').names;

const AppShell = styled.div`
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  box-sizing: border-box;
  background: ${({ $nameType }) => {
    if ($nameType === 'girl') return '#fff4f8';
    if ($nameType === 'boy') return '#f2f7ff';
    return '#ffffff';
  }};
`;

const ContentColumn = styled.div`
  width: min(100%, 32rem);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  text-align: center;
`;

const Title = styled(Box)`
  margin: 8px 0 16px;
`;

const mobileBreakpoint = '768px';

const ResponsiveAppShell = styled(AppShell)`
  @media (max-width: ${mobileBreakpoint}) {
    padding: 0;
  }
`;

const ResponsiveContentColumn = styled(ContentColumn)`
  @media (max-width: ${mobileBreakpoint}) {
    width: 100%;
    min-height: 100dvh;
    padding: 24px 16px;
    box-sizing: border-box;
  }
`;

function App () {
  const [user, setUser] = useState(undefined);

  const [scores, setScores] = useState({});

  const [nameCount, setNameCount] = useState(undefined);

  const [savedGame, setSavedGame] = useState(null);

  const [nameType, setNameType] = useState(undefined);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((nextUser) => {
      setUser(nextUser);
    });

    return unsubscribe;
  }, []);

  const handleLogin = function() {
    firebase.auth().signInWithRedirect(provider);
  };

  const shuffle = useCallback((items) => {
    const copy = [...items];

    for (let index = copy.length - 1; index > 0; index--) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      const current = copy[index];
      copy[index] = copy[swapIndex];
      copy[swapIndex] = current;
    }

    return copy;
  }, []);

  const names = useMemo(() => {
    const allNames = nameType === 'girl' ? girlsNames : boysNames;

    const namesWithScores = allNames.map((name) => {
      const score = scores[name] || {};

      return {
        name: name,
        views: score.views || 0,
        votes: score.votes || 0,
        hidden: score.hidden || false,
        ...score
      }
    });

    const namesWithRanks = namesWithScores.map((row) => {
      const rank = ((row.votes + 1.9208) / (row.votes + row.views) - 1.96 *
				Math.sqrt((row.votes * row.views) / (row.votes + row.views) + 0.9604) /
				(row.votes + row.views)) / (1 + 3.8416 / (row.votes + row.views));

      return {
        ...row,
        rank: rank ? rank : 0
      };
    }).sort((a, b) => {
      return a.rank - b.rank
    });

    namesWithRanks.reverse();

    return namesWithRanks;
  }, [scores, nameType]);

  const saveNameType = useCallback((selectedNameType) => {
    setNameType(selectedNameType);

    return firebase
      .database()
      .ref(`users/${user.uid}/profile`)
      .update({
        nameType: selectedNameType
      });
  }, [user]);

  const buildGameState = useCallback((roundNames, currentRound, currentRoundNamesIndex) => {
    return {
      nameType: nameType,
      currentRound: currentRound,
      currentRoundNamesIndex: currentRoundNamesIndex,
      totalRounds: Math.floor(roundNames.length / 2),
      selectedNameCount: roundNames.length,
      roundNames: roundNames
    };
  }, [nameType]);

  const persistGameState = useCallback((gameState) => {
    firebase
      .database()
      .ref(`users/${user.uid}/currentGame`)
      .set(gameState);

    setSavedGame(gameState);

    return gameState;
  }, [user]);

  const chooseRoundNames = useCallback((items, roundNumber) => {
    const ranked = items
      .map(item => {
        const lower = wilsonLower(item.votes, item.views);
        const upper = wilsonUpper(item.votes, item.views);
        return { ...item, lower, upper, n: item.votes + item.views };
      })
      .sort((a, b) => b.lower - a.lower);

    const keepFraction = Math.max(0.05, Math.pow(0.55, roundNumber));
    const targetKeep = Math.max(20, Math.ceil(ranked.length * keepFraction));
    const cutoffLower = ranked[Math.min(targetKeep - 1, ranked.length - 1)].lower;

    return ranked.filter((item, index) => {
      if (item.n < 3) return true;
      if (index < targetKeep) return true;
      return item.upper >= cutoffLower;
    });
  }, []);

  const handleRoundStart = useCallback(({ currentRound, currentRoundNamesIndex = 0 }) => {
    const selectedItems = chooseRoundNames(names, currentRound);

    if (!selectedItems || selectedItems.length < 2) {
      return null;
    }

    const roundNames = shuffle(selectedItems).map((item) => item.name);

    if (!roundNames) {
      return null;
    }

    return persistGameState(buildGameState(roundNames, currentRound, currentRoundNamesIndex));
  }, [buildGameState, chooseRoundNames, names, persistGameState, shuffle]);

  const handleMatchChange = useCallback(({ roundNames, currentRound, currentRoundNamesIndex }) => {
    return persistGameState(buildGameState(roundNames, currentRound, currentRoundNamesIndex));
  }, [buildGameState, persistGameState]);

  const handlePlay = function ({ nameType: selectedNameType }) {
    const nextNameType = selectedNameType || nameType;

    saveNameType(nextNameType)
      .then(() => firebase
        .database()
        .ref(`users/${user.uid}/currentGame`)
        .once('value')
        .then((snapshot) => snapshot.val()))
      .then((currentGame) => {
        if (
          currentGame &&
          currentGame.nameType === nextNameType &&
          currentGame.roundNames &&
          currentGame.roundNames.length >= 2
        ) {
          setSavedGame(currentGame);
          setNameCount(currentGame.selectedNameCount);
          return;
        }

        const nextGame = handleRoundStart({ currentRound: 0, currentRoundNamesIndex: 0 });

        if (!nextGame) {
          startNewGame();
          return;
        }

        setNameCount(nextGame.selectedNameCount);
      });
  };

  const handlePick = function ({ data }) {
    data.forEach((save) => {
      firebase
        .database()
        .ref(`users/${user.uid}/scores/${save.name}`)
        .set({
          views: save.views,
          votes: save.votes
        });
    });
  };

  const handlePickNames = (updates) => {
    const save = Object.keys(updates).reduce((carry, name) => {
      carry[name] = {
        ...scores[name],
        hidden: updates[name].hidden
      };
      return carry;
    }, {});

    firebase
      .database()
      .ref(`users/${user.uid}/scores`)
      .update(save);

    setScores((currentScores) => Object.keys(updates).reduce((carry, name) => {
      carry[name] = {
        ...carry[name],
        hidden: updates[name].hidden
      };
      return carry;
    }, { ...currentScores }));
  };

  const loadUserScores = useCallback(() => {
      firebase
        .database()
        .ref(`users/${user.uid}/scores`)
        .once('value')
        .then((snapshot) => snapshot.val())
        .then(data => {
          if (!data) {
            setScores({});
          } else {
            setScores(data);
          }
        });
  }, [user]);

  const startNewGame = useCallback(function () {
    setNameCount(undefined);
    setSavedGame(null);

    if (user) {
      firebase
        .database()
        .ref(`users/${user.uid}/currentGame`)
        .remove();
    }

    loadUserScores();
  }, [user, loadUserScores]);

  useEffect(() => {
    if (!user) {
      setScores({});
      setNameType(undefined);
      return;
    }

    firebase
      .database()
      .ref(`users/${user.uid}/profile/nameType`)
      .once('value')
      .then((snapshot) => snapshot.val())
      .then((savedNameType) => {
        setNameType(savedNameType || null);
      });

    loadUserScores();
  }, [user, loadUserScores]);

  const handleChooseNameType = (selectedNameType) => {
    saveNameType(selectedNameType);
  };

  function wilsonLower(wins, losses, z = 1.96) {
  const n = wins + losses;
  if (n === 0) return 0;
  const p = wins / n;
  const z2 = z * z;
  return (
    (p + z2 / (2 * n) - z * Math.sqrt((p * (1 - p) + z2 / (4 * n)) / n)) /
    (1 + z2 / n)
  );
}

function wilsonUpper(wins, losses, z = 1.96) {
  const n = wins + losses;
  if (n === 0) return 1;
  const p = wins / n;
  const z2 = z * z;
  return (
    (p + z2 / (2 * n) + z * Math.sqrt((p * (1 - p) + z2 / (4 * n)) / n)) /
    (1 + z2 / n)
  );
}

  const ss = {
    display: 'inline-block',
    fontWeight: 'bold',
    fontSize: '2em',
    letterSpacing: '2px',
    transform: 'rotate(5deg)'
  };

  return (
    <div className="App">
      <ResponsiveAppShell $nameType={nameType}>
        <ResponsiveContentColumn>
          <Title>
            <span style={ss}>N</span>
            <span style={ss}>a</span>
            <span style={ss}>m</span>
            <span style={ss}>e</span>&nbsp;
            <span style={ss}>G</span>
            <span style={ss}>a</span>
            <span style={ss}>m</span>
            <span style={ss}>e</span>
          </Title>

          {!user && <Button fullWidth variant="contained" onClick={handleLogin} sx={{ m: 1 }}>Login with Google</Button>}

          {user && nameType && names && !nameCount && <Lobby onPlay={handlePlay} nameType={nameType} names={names} onPickNames={handlePickNames}/>}

          {user && nameType && nameCount && <Game names={names} savedGame={savedGame} onPick={handlePick} onRoundStart={handleRoundStart} onMatchChange={handleMatchChange} startNewGame={startNewGame}/>}
        </ResponsiveContentColumn>
      </ResponsiveAppShell>

      <Dialog
        open={Boolean(user) && nameType === null}
        aria-labelledby="name-type-dialog-title"
        aria-describedby="name-type-dialog-description"
      >
        <DialogTitle id="name-type-dialog-title">Choose boy or girl names</DialogTitle>
        <DialogContent>
          <DialogContentText id="name-type-dialog-description">
            Before the first game, choose which list of names you want to play with.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => handleChooseNameType('boy')}>Boy</Button>
          <Button variant="contained" onClick={() => handleChooseNameType('girl')}>Girl</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default App;
